import type { SealState, Seals, World } from '../types';
import type { SiteId } from '../data/sites';
import { MONK_SEAL, stepMonkSeals } from '../seals';
import { MONK_SEAL_GROWTH, MONK_SEAL_TABLE, MONK_SEAL_TABLE_YEAR } from '../data/seals/monkseal';
import { STREAM, stockStream } from '../streams';
import { EXPECTED_DRAWS, stockDraws, type PopulationDraws } from '../population';
import { civilFromEpochDay, epochDayFromCivil, epochDayOf } from '../calendar';

/** The monk seals' draws on their stock's stream of the day (`stockDraws`, one stock): binomial deaths on each class's and each day's nursing pups, a Poisson draw of the day's pups. */
export function sealDraws(world: { seed: number; day: number }, siteId: SiteId, streamOf: typeof stockStream = stockStream): PopulationDraws {
  return stockDraws(world, STREAM.monkSeal, siteId, streamOf);
}

/**
 * Step 3 of the tick, the animals, after the humpbacks (M103): each
 * sanctuary's monk seals are born, weaned and die (`seals.ts`), on their own
 * stream of the day (a test hands in its own as `streamOf`).
 */
export function stepSeals(world: World, streamOf: typeof stockStream = stockStream): void {
  const epochDay = epochDayOf(world);
  for (const s of world.sanctuaries) {
    if (s.seals) stepMonkSeals(MONK_SEAL, s.seals.monk, epochDay, sealDraws(world, s.siteId, streamOf));
  }
}

/** Whether a site has monk seals: Maui Nui, of the main Hawaiian Islands. */
export const siteHasSeals = (siteId: SiteId): boolean => siteId === 'maui_nui';

/**
 * Whole seals from expected ones, their total the expected total rounded:
 * each its floor, then one more to those with the largest remainders, the
 * first first where two are level. Rounded each alone, the nursing pups,
 * every day's under half a pup, would all be lost.
 */
export function wholeSeals(xs: readonly number[]): number[] {
  const out = xs.map((x) => Math.floor(x));
  const left = Math.round(xs.reduce((a, b) => a + b, 0)) - out.reduce((a, b) => a + b, 0);
  const order = xs.map((x, i) => ({ i, r: x - Math.floor(x) })).sort((a, b) => b.r - a.r || a.i - b.i);
  for (let k = 0; k < left; k++) {
    const i = (order[k] as { i: number }).i;
    out[i] = (out[i] as number) + 1;
  }
  return out;
}

/**
 * The monk seals as a world opens on a day: the table's (`data/seals/`, the
 * census year's last day, its stable ages), grown by its factor a year to the
 * opening year's first of January (a whole number of the table's years, before
 * or after it), stepped at expected values through the opening year's days to
 * the opening day, the classes and the nursing pups each made whole seals
 * (`wholeSeals`; no draw: a world's opening draws nothing).
 */
export function openingSeals(epochDay: number): Seals {
  const year = civilFromEpochDay(epochDay).year;
  const grown = MONK_SEAL_GROWTH ** (year - MONK_SEAL_TABLE_YEAR);
  const monk: SealState = { counts: MONK_SEAL_TABLE.counts.map((n) => n * grown), nursing: MONK_SEAL_TABLE.nursing.map((n) => n * grown) };
  for (let d = epochDayFromCivil(year, 1, 1); d <= epochDay; d++) stepMonkSeals(MONK_SEAL, monk, d, EXPECTED_DRAWS);
  return { monk: { counts: wholeSeals(monk.counts), nursing: wholeSeals(monk.nursing) } };
}

/** A sanctuary's seals as a world opens on a day, where its site has them (`siteHasSeals`); elsewhere none. */
export function sanctuaryOpeningSeals(siteId: SiteId, epochDay: number): Seals | null {
  return siteHasSeals(siteId) ? openingSeals(epochDay) : null;
}
